// MorrowOS Knowledge base surface — the ever-growing profile document MorrowOS
// builds about the user, rendered from server-side markdown.
import {
  KnowledgeIcon,
  LayersIcon,
  ListChecksIcon,
  PenLineIcon,
  RefreshCwIcon,
  SparklesIcon,
} from "lucide-react";
import { useUser } from "@clerk/react";
import { useCallback, useEffect, useState } from "react";
import { useSearch, useNavigate } from "@tanstack/react-router";

import { useI18n } from "~/i18n";
import { hasClerkPublishableKey } from "../../cloud/publicConfig";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { resolveComposioUserId } from "../../composioConnect";
import {
  fetchKnowledgeDoc,
  knowledgeErrorMessageKey,
  type KnowledgeErrorCode,
  type KnowledgeProbeState,
  type PersonalKnowledgeKind,
} from "../../morrowKnowledge";
import ChatMarkdown from "../ChatMarkdown";
import { Button } from "../ui/button";
import { MorrowDocumentSkeleton } from "../morrow/MorrowPageSkeletons";
import { MorrowDocumentColumn } from "../workspace/MorrowDocumentColumn";
import { MorrowPersonIdentityCard } from "../workspace/MorrowIdentityCard";
import { MorrowWorkspacePage } from "../workspace/MorrowWorkspacePage";
import { buildPersonalIdentity } from "../workspace/morrowMemberAvatarResolve";
import { ensureUserAvatarSeed, useUserProfile } from "../workspace/morrowUserProfileStore";
import { MorrowMemoryToggleButton } from "../memory/MorrowMemoryToggleButton";
import { MorrowScopedMemoryPanel } from "../memory/MorrowMemoryPage";
import { MemoryDiagnostics } from "../memory/MemoryDiagnostics";

/**
 * Which knowledge doc the page is showing. The Knowledge Base is PERSONAL only —
 * the three companion docs the agent maintains about the user (facts / style /
 * preferences). Organizational memory is NOT here; it lives in the Organization
 * area (the Team page), keeping personal knowledge cleanly separate from company
 * knowledge.
 */
type KnowledgeTab = PersonalKnowledgeKind;

// Illustrative categories the profile grows into — hints, not real data.
const EMPTY_CATEGORIES = [
  {
    key: "knowledge.catPreferences",
    hintKey: "knowledge.catPreferencesHint",
    Icon: SparklesIcon,
  },
  { key: "knowledge.catProjects", hintKey: "knowledge.catProjectsHint", Icon: LayersIcon },
  { key: "knowledge.catGoals", hintKey: "knowledge.catGoalsHint", Icon: ListChecksIcon },
  { key: "knowledge.catStyle", hintKey: "knowledge.catStyleHint", Icon: PenLineIcon },
] as const;

export function KnowledgeBasePage() {
  return <KnowledgeBasePageBody />;
}

function KnowledgeBasePageBody() {
  const { t } = useI18n();
  const navigate = useNavigate({ from: "/knowledge" });
  const { tab: tabFromSearch, view: viewFromSearch } = useSearch({ strict: false });
  const showMemory = viewFromSearch === "memory";
  const { isLoaded: clerkLoaded, isSignedIn, userId: clerkUserId } = useMorrowClerkAuth();
  // Same stable per-user id the greeting uses: Clerk's userId when signed in,
  // otherwise a persisted guest id (see resolveComposioUserId).
  const userId = resolveComposioUserId(clerkLoaded && isSignedIn ? clerkUserId : null);

  const [tab, setTab] = useState<KnowledgeTab>("profile");
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<KnowledgeErrorCode | null>(null);
  const [loading, setLoading] = useState(true);
  const [probeState, setProbeState] = useState<KnowledgeProbeState>("idle");

  useEffect(() => {
    if (
      tabFromSearch === "profile" ||
      tabFromSearch === "behavior" ||
      tabFromSearch === "preferences"
    ) {
      setTab(tabFromSearch);
    }
  }, [tabFromSearch]);

  const activeTab: KnowledgeTab = tab;

  // The three personal docs — always the same set (no org tab here anymore).
  const tabs: ReadonlyArray<{ key: KnowledgeTab; label: string }> = [
    { key: "profile", label: t("knowledge.tabAboutYou") },
    { key: "behavior", label: t("knowledge.tabStyle") },
    { key: "preferences", label: t("knowledge.tabPreferences") },
  ];
  const load = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      const doc = await fetchKnowledgeDoc(userId, activeTab);
      setMarkdown(doc.markdown);
      setError(doc.error);
      setErrorCode(doc.errorCode);
      setProbeState(doc.probeState);
      if (!quiet) setLoading(false);
    },
    [userId, activeTab],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // While the probe is still gathering, quietly re-poll so the enriched doc
  // appears on its own (no spinner, no manual Refresh needed).
  useEffect(() => {
    if (probeState !== "running") return;
    const timer = setInterval(() => void load(true), 5000);
    return () => clearInterval(timer);
  }, [probeState, load]);

  const toggleMemoryView = useCallback(() => {
    void navigate({
      search: (current) => {
        if (showMemory) {
          const { view: _view, ...rest } = current as { view?: string };
          return rest;
        }
        return { ...current, view: "memory" as const };
      },
      replace: true,
    });
  }, [navigate, showMemory]);

  return (
    <MorrowWorkspacePage
      title={t("knowledge.title")}
      subtitle={showMemory ? t("memory.subtitlePersonal") : t("knowledge.subtitle")}
      icon={<KnowledgeIcon />}
      wide
      toolbar={
        showMemory ? null : (
          <div className="flex flex-wrap gap-4 text-sm font-medium">
            {tabs.map((entry) => (
              <button
                key={entry.key}
                type="button"
                onClick={() => setTab(entry.key)}
                className={`border-b-2 pb-2 transition-colors ${
                  activeTab === entry.key
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {entry.label}
              </button>
            ))}
          </div>
        )
      }
      action={
        <div className="flex items-center gap-2">
          <MorrowMemoryToggleButton active={showMemory} onClick={toggleMemoryView} />
          {showMemory ? null : (
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              onClick={() => void load()}
              disabled={loading}
              aria-label={loading ? t("common.loading") : t("common.refresh")}
              title={loading ? t("common.loading") : t("common.refresh")}
            >
              <RefreshCwIcon className={loading ? "animate-spin" : ""} aria-hidden="true" />
            </Button>
          )}
        </div>
      }
    >
      <MorrowDocumentColumn aside={<KnowledgeIdentityAside />}>
        {showMemory ? (
          <>
            <MemoryDiagnostics />
            <MorrowScopedMemoryPanel scope="personal" />
          </>
        ) : (
          <div key={activeTab} className="motion-reduce:animate-none animate-morrow-page-enter">
            {errorCode ? (
              <div className="rounded-lg border border-border bg-muted p-4 text-sm font-medium text-foreground">
                {t(knowledgeErrorMessageKey(errorCode))}
              </div>
            ) : error ? (
              <div className="rounded-lg border border-border bg-muted p-4 text-sm font-medium text-foreground">
                {error}
              </div>
            ) : null}

            {!loading && probeState === "running" ? (
              <div className="mb-4 flex items-center gap-2.5 rounded-lg border border-border/60 bg-primary/5 px-4 py-3 text-sm text-foreground">
                <RefreshCwIcon className="size-3.5 shrink-0 animate-spin text-primary" />
                <span>
                  MorrowOS is learning about you — gathering from your connected apps. This updates
                  automatically.
                </span>
              </div>
            ) : null}

            {!loading && probeState === "failed" ? (
              <div className="mb-4 flex items-center gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-foreground">
                <SparklesIcon className="size-3.5 shrink-0 text-amber-500" />
                <span>
                  MorrowOS couldn’t gather more from your apps just now — it’ll keep trying.
                  {markdown ? " Here’s what it knows so far." : ""}
                </span>
              </div>
            ) : null}

            {loading ? (
              <MorrowDocumentSkeleton />
            ) : markdown ? (
              <LoadedState markdown={markdown} />
            ) : activeTab === "profile" ? (
              <EmptyState
                title={t("knowledge.emptyTitle")}
                body={t("knowledge.empty")}
                categoriesLabel={t("knowledge.emptyCategoriesLabel")}
                categories={EMPTY_CATEGORIES.map(({ key, hintKey, Icon }) => ({
                  label: t(key),
                  hint: t(hintKey),
                  Icon,
                }))}
              />
            ) : (
              <TabEmptyState
                title={t("knowledge.emptyTabTitle")}
                body={t("knowledge.emptyTabBody")}
              />
            )}
          </div>
        )}
      </MorrowDocumentColumn>
    </MorrowWorkspacePage>
  );
}

function KnowledgeIdentityAside() {
  if (hasClerkPublishableKey()) return <ClerkKnowledgeIdentityAside />;
  return <LocalKnowledgeIdentityAside />;
}

function ClerkKnowledgeIdentityAside() {
  const { user } = useUser();
  const profile = useUserProfile();
  const { t } = useI18n();
  const identity = buildPersonalIdentity({
    user,
    profile,
    fallbackName: t("team.you"),
  });

  useEffect(() => {
    ensureUserAvatarSeed();
  }, []);

  return (
    <MorrowPersonIdentityCard
      name={identity.personName}
      title={identity.title}
      role={identity.role}
      email={identity.personEmail}
      {...(identity.avatar.avatarSeed ? { avatarSeed: identity.avatar.avatarSeed } : {})}
      {...(identity.memberSince ? { memberSince: identity.memberSince } : {})}
      description={t("identity.knowledgeHint")}
    />
  );
}

function LocalKnowledgeIdentityAside() {
  const profile = useUserProfile();
  const { t } = useI18n();
  const identity = buildPersonalIdentity({
    profile,
    fallbackName: t("team.you"),
  });

  useEffect(() => {
    ensureUserAvatarSeed();
  }, []);

  return (
    <MorrowPersonIdentityCard
      name={identity.personName}
      title={identity.title}
      role={identity.role}
      {...(identity.avatar.avatarSeed ? { avatarSeed: identity.avatar.avatarSeed } : {})}
      description={t("identity.knowledgeHint")}
    />
  );
}

function LoadedState({ markdown }: { markdown: string }) {
  return <ChatMarkdown text={markdown} cwd={undefined} documentSurface />;
}

/** Compact per-tab empty state ("Nothing learned yet") for the non-facts docs. */
function TabEmptyState({ title, body }: { title: string; body: string }) {
  return (
    <section className="flex flex-col gap-3 py-6">
      <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <SparklesIcon className="size-5" />
      </div>
      <div className="min-w-0">
        <h2 className="text-base font-semibold tracking-tight text-foreground">{title}</h2>
        <p className="mt-2 max-w-prose text-sm leading-6 text-muted-foreground">{body}</p>
      </div>
    </section>
  );
}

function EmptyState({
  title,
  body,
  categoriesLabel,
  categories,
}: {
  title: string;
  body: string;
  categoriesLabel: string;
  categories: ReadonlyArray<{ label: string; hint: string; Icon: typeof SparklesIcon }>;
}) {
  return (
    <section className="flex flex-col gap-10 py-4">
      <div className="flex gap-4">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <KnowledgeIcon className="size-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold tracking-tight text-foreground">{title}</h2>
          <p className="mt-2 max-w-prose text-sm leading-6 text-muted-foreground">{body}</p>
        </div>
      </div>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          {categoriesLabel}
        </h3>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {categories.map(({ label, hint, Icon }) => (
            <li
              key={label}
              className="flex min-h-[5.5rem] gap-3 rounded-xl border border-border/60 bg-muted/20 p-4"
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon className="size-4" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{label}</p>
                <p className="mt-1 text-xs leading-5 text-muted-foreground">{hint}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
